// @vendors
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    UserIcon,
    EnvelopeIcon,
    PhoneIcon,
    HomeIcon,
    LockClosedIcon,
    EyeIcon,
    EyeSlashIcon
} from "@heroicons/react/24/outline";

// @store
import { useCompanyStore } from '../../../../store/useCompanyStore';
import { useSpinnerStore } from '../../../../store/useSpinnerStore';

// @services
import { updateDataCompany } from '../../../../helpers/functionsUtils';
import { IFormDataCompany } from '../../../../types/professional.types';
import { UserRole } from '../../../../types/auth.types';

const FormCompanyInfo = () => {
    const { data, getProfessional } = useCompanyStore();
    const { t } = useTranslation();
    const { openSpinner } = useSpinnerStore();
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [formValue, setFormValue] = useState<IFormDataCompany>({
        name: data?.name ?? "",
        desiredName: data?.desiredName ?? "",
        emergencyContactName: data?.emergencyContactName ?? "",
        address: data?.location.address ?? "",
        password: "",
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormValue((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    }

    const updateData = async (field: keyof IFormDataCompany) => {
        const params = {
            field: field,
            formValue,
            openSpinner,
            t,
            getProfessional,
            userType: UserRole.PROFESSIONAL,
            userId: data?.professionalId ?? ''
        };
        return updateDataCompany(params);
    }

    return (
        <div className="flex flex-col p-6 bg-white dark:bg-dark-cBackground rounded-lg shadow-md w-full h-[85vh]">
            <h3 className="font-semibold mb-3 dark:text-dark-cText">
                {t("professional.profile.titleForm")}
            </h3>
            <div className="space-y-4">
                <div>
                    <label className="text-sm font-medium text-[#262422] dark:text-dark-cText/80 dark:text-dark-cTextSecondary">
                        {t("professional.profile.legalName")}
                    </label>
                    <div className="flex flex-row gap-1">
                        <UserIcon className='w-[25px] mr-[5px] text-[#ABABAB]' />
                        <input
                            type="text"
                            name='name'
                            value={formValue.name}
                            onChange={(e) => handleChange(e)}
                            className="w-full py-2 border-b dark:bg-dark-cBackground text-cTextSecondary/50 dark:text-dark-cTextSecondary/60 placeholder:text-cTextSecondary/50 dark:placeholder-dark-cTextSecondary/50 focus:outline-none focus:ring-0 disabled:bg-white"
                            disabled={false}
                        />
                        <button
                            className="text-sm text-[#F97068]"
                            onClick={() => updateData('name')}
                        >
                            {t("professional.profile.buttonEdit")}
                        </button>
                    </div>
                </div>

                <div>
                    <label className="text-sm font-medium text-[#262422] dark:text-dark-cText/80 dark:text-dark-cTextSecondary">
                        {t("professional.profile.desiredName")}
                    </label>
                    <div className="flex flex-row gap-1">
                        <UserIcon className='w-[25px] mr-[5px] text-[#ABABAB]' />
                        <input
                            type="text"
                            name='desiredName'
                            value={formValue.desiredName}
                            onChange={(e) => handleChange(e)}
                            className="w-full py-2 border-b dark:bg-dark-cBackground text-cTextSecondary/50 dark:text-dark-cTextSecondary/60 placeholder:text-cTextSecondary/50 dark:placeholder-dark-cTextSecondary/50 focus:outline-none focus:ring-0 disabled:bg-white"
                            //placeholder={'placeholder'}
                            disabled={false}
                        />
                        <button
                            className="text-sm text-[#F97068]"
                            onClick={() => updateData('desiredName')}
                        >
                            {t("professional.profile.buttonEdit")}
                        </button>
                    </div>
                </div>

                <div>
                    <label className="text-sm font-medium text-[#262422] dark:text-dark-cText/80 dark:text-dark-cTextSecondary">
                        {t("professional.profile.email")}
                    </label>
                    <div className="flex flex-row gap-1">
                        <EnvelopeIcon className='w-[25px] mr-[5px] text-[#ABABAB]' />
                        <input
                            type="text"
                            name='email'
                            value={data?.email}
                            className="w-full py-2 border-b dark:bg-dark-cBackground text-cTextSecondary/50 dark:text-dark-cTextSecondary/60 placeholder:text-cTextSecondary/50 dark:placeholder-dark-cTextSecondary/50 focus:outline-none focus:ring-0 disabled:bg-white"
                            disabled={true}
                        />
                        <div className="w-[40px]"></div>
                    </div>
                </div>

                <div>
                    <label className="text-sm font-medium text-[#262422] dark:text-dark-cText/80 dark:text-dark-cTextSecondary">
                        {t("professional.profile.phone")}
                    </label>
                    <div className="flex flex-row gap-1">
                        <PhoneIcon className='w-[25px] mr-[5px] text-[#ABABAB]' />
                        <input
                            type="text"
                            name='phone'
                            value={data?.phone}
                            className="w-full py-2 border-b dark:bg-dark-cBackground text-cTextSecondary/50 dark:text-dark-cTextSecondary/60 placeholder:text-cTextSecondary/50 dark:placeholder-dark-cTextSecondary/50 focus:outline-none focus:ring-0 disabled:bg-white"
                            //placeholder={'placeholder'}
                            disabled={true}
                        />
                        <div className="w-[40px]"></div>
                    </div>
                </div>

                <div>
                    <label className="text-sm font-medium text-[#262422] dark:text-dark-cText/80 dark:text-dark-cTextSecondary">
                        {t("professional.profile.emergencyContact")}
                    </label>
                    <div className="flex flex-row gap-1">
                        <UserIcon className='w-[25px] mr-[5px] text-[#ABABAB]' />
                        <input
                            type="text"
                            name='emergencyContactName'
                            value={formValue.emergencyContactName}
                            onChange={(e) => handleChange(e)}
                            className="w-full py-2 border-b dark:bg-dark-cBackground text-cTextSecondary/50 dark:text-dark-cTextSecondary/60 placeholder:text-cTextSecondary/50 dark:placeholder-dark-cTextSecondary/50 focus:outline-none focus:ring-0 disabled:bg-white"
                            //placeholder={'placeholder'}
                            disabled={false}
                        />
                        <button
                            className="text-sm text-[#F97068]"
                            onClick={() => updateData('emergencyContactName')}
                        >
                            {t("professional.profile.buttonEdit")}
                        </button>
                    </div>
                </div>

                <div>
                    <label className="text-sm font-medium text-[#262422] dark:text-dark-cText/80 dark:text-dark-cTextSecondary">
                        {t("professional.profile.address")}
                    </label>
                    <div className="flex flex-row gap-1">
                        <HomeIcon className='w-[25px] mr-[5px] text-[#ABABAB]' />
                        <input
                            type="text"
                            name='address'
                            value={formValue.address}
                            onChange={(e) => handleChange(e)}
                            className="w-full py-2 border-b dark:bg-dark-cBackground text-cTextSecondary/50 dark:text-dark-cTextSecondary/60 placeholder:text-cTextSecondary/50 dark:placeholder-dark-cTextSecondary/50 focus:outline-none focus:ring-0 disabled:bg-white"
                            disabled={false}
                        />
                        <button
                            className="text-sm text-[#F97068]"
                            onClick={() => updateData('address')}
                        //onClick={() => updateAddress()}
                        >
                            {t("professional.profile.buttonEdit")}
                        </button>
                    </div>
                </div>

                <div>
                    <label className="text-sm font-medium text-[#262422] dark:text-dark-cText/80 dark:text-dark-cTextSecondary">
                        {t("professional.profile.password")}
                    </label>
                    <div className="flex flex-row gap-1">
                        <LockClosedIcon className='w-[25px] mr-[5px] text-[#ABABAB]' />
                        <input
                            type={passwordVisible ? "text" : "password"}
                            name='password'
                            value={formValue.password}
                            onChange={(e) => handleChange(e)}
                            className="w-full py-2 border-b dark:bg-dark-cBackground text-cTextSecondary/50 dark:text-dark-cTextSecondary/60 placeholder:text-cTextSecondary/50 dark:placeholder-dark-cTextSecondary/50 focus:outline-none focus:ring-0 disabled:bg-white"
                            disabled={true}
                        />
                        {passwordVisible
                            ? <EyeSlashIcon className='w-[25px] mr-[5px] text-[#ABABAB]' onClick={() => setPasswordVisible(!passwordVisible)} />
                            : <EyeIcon className='w-[25px] mr-[5px] text-[#ABABAB]' onClick={() => setPasswordVisible(!passwordVisible)} />
                        }
                        <button className="text-sm text-[#F97068]">
                            {t("professional.profile.chengePass")}
                        </button>
                    </div>
                </div>

            </div>
        </div>
    )
}

export default FormCompanyInfo